<i18n>
{
  "en": {
    "menuPreviewTitle": "Menu preview",
    "selectService": "Select a service",
    "selectMenu": "Select a menu",
    "selectZone": "Select a zone",
    "menuLoadingError": "Error loading the menus",
    "cacheLoadingError": "Error loading the menu data",
    "cacheLoading": "Loading menu data, please wait",
    "previewModalSubtitle": "Select a day, a service and an area to consult the appearance and prices of the menu",
    "dataPickerRequired": "Select a date"
  },
  "es": {
    "menuPreviewTitle": "Previsualización del menú",
    "selectService": "Selecciona un servicio",
    "selectMenu": "Selecciona un menú",
    "selectZone": "Selecciona una zona",
    "menuLoadingError": "Error cargando los menús",
    "cacheLoadingError": "Error cargando los datos del menu",
    "cacheLoading": "Cargando datos de menu, por favor espere",
    "previewModalSubtitle": "Selecciona un día, un servicio y una zona para consultar la apariencia y precios del menú",
    "dataPickerRequired": "Selecciona una fecha"
  }
}
</i18n>

<template>
  <div>
    <v-dialog @click:outside="onCancel" v-model="visible" max-width="600px" scrollable persistent>
      <div v-if="showSpinner">
        <dynameat-spinner/>
        <p class="text-center">{{ $t('cacheLoading') }}</p>
      </div>
      <v-card v-else height="100%" class="px-7 py-5">
        <div class="d-flex align-center justify-space-between">
          <v-card-title class="word-break pa-0">
            {{ $t('menuPreviewTitle') }}
          </v-card-title>
        </div>
        <v-card-subtitle class="pa-0 mb-3">{{ $t('previewModalSubtitle') }}</v-card-subtitle>
        <v-container class="pa-0">
          <v-form class="d-flex flex-column align-left" ref="form">
            <v-card-subtitle class="pa-0 ma-0 mt-1 mb-1 text-center">{{ $t('dataPickerRequired') + ' *' }}</v-card-subtitle>
            <date-picker class="inline-block h-full mx-auto" v-model="date"
                         trim-weeks :locale="userLocale" ref="datepicker"/>
            <v-select v-model="selectedService" :items="serviceList"
                      :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectService')" item-value="guid" item-text="name" return-object
                      class="required"/>
            <v-select v-model="selectedMenu" :items="managedMenuList"
                      :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectMenu')" item-value="guid" item-text="title" return-object
                      class="required"/>
            <v-select v-model="selectedZone" :items="menuZones" :menu-props="{ maxHeight: '400' }"
                      :label="$t('selectZone')" :disabled="!isMenuSelected" item-value="guid"
                      item-text="name" return-object class="required"/>
            <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm"
                                    :confirmDisabled="!formValid"
                                    :confirmText="$t('confirm')" class="mt-2"/>
          </v-form>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import DynameatSpinner from '@dynameat/components/DynameatSpinner';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';

@Component
@Components({ DatePicker, DynameatSpinner, DynameatModalButtons })
@MapGetters(['menuList', 'saleZoneList', 'serviceList', 'isLoading', 'templateList', 'saleRateList',
  'userLocale'])
@MapActions(['menuListRequest', 'addNotification', 'setLoading', 'templateListRequest'])
export default class MenuPreviewModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  valid = false;

  date = null

  selectedMenu = null;

  selectedZone = null;

  selectedService = null;

  get managedMenuList() {
    return this.menuList.filter((el) => el.is_managed);
  }

  get showSpinner() {
    return this.visible && this.isLoading;
  }

  get isMenuSelected() {
    return !!this.selectedMenu;
  }

  get isDaySelected() {
    return !!this.date;
  }

  get isZoneSelected() {
    return !!this.selectedZone;
  }

  get isServiceSelected() {
    return !!this.selectedService;
  }

  get menuTemplate() {
    if (this.isMenuSelected) {
      return this.templateList.find((temp) => temp.guid === this.selectedMenu.template);
    }
    return null;
  }

  get menuZones() {
    const saleRateNameArray = [];
    const localZoneList = this.isMenuSelected ? this.saleZoneList.filter((zone) => this.selectedMenu.sale_zones?.includes(zone.guid)) : this.saleZoneList;
    localZoneList.forEach((zone) => {
      const saleRate = this.saleRateList.find((rate) => rate.sale_zones?.find((el) => el.guid === zone.guid));
      const rateName = saleRate?.saleZoneNames;
      if (!saleRateNameArray.find((el) => el.name === rateName)) {
        saleRateNameArray.push({ name: zone.name, guid: zone.guid }); // We are using one tariff
        // saleRateNameArray.push({ name: rateName, guid: zone.guid });
      }
    });
    return saleRateNameArray;
  }

  get formValid() {
    return this.isMenuSelected && this.isDaySelected && this.isZoneSelected && this.isServiceSelected;
  }

  @Watch('visible')
  async onVisible(value) {
    if (value) {
      await this.loadMenuData();
    }
  }

  async onConfirm() {
    if (this.formValid) {
      await this.showPreview();
      this.$emit('confirm');
    }
  }

  onCancel() {
    this.clearOwnData();
    this.$emit('cancel');
  }

  clearOwnData() {
    this.date = null;
    this.selectedMenu = null;
    this.selectedService = null;
    this.selectedZone = null;
  }

  async loadMenuData() {
    try {
      await this.setLoading(1);
      await this.menuListRequest(this.$route.params.pointOfSaleGuid);
      await this.templateListRequest();
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('menuLoadingError')));
    } finally {
      await this.setLoading(-1);
    }
  }

  async showPreview() {
    try {
      this.addNotification(createSuccessNotification(this.$t('cacheLoading'), {}));
      await this.setLoading(1);
      const routeData = this.$router.resolve({
        name: 'menu-render-view',
        params: {
          menuGuid: this.selectedMenu.guid,
          zoneGuid: this.selectedZone.guid,
          serviceGuid: this.selectedService.guid,
          year: this.date.getFullYear(),
          month: this.date.getMonth() + 1,
          day: this.date.getDate()
        }
      });
      const { width, height } = this.menuTemplate.getWidthAndHeight();
      window.open(routeData.href, '_blank', `toolbar=0,menubar=0,resizable=0, location=0,status=0,dependent=1,scrollbars=0,titlebar=0,width=${width},height=${height}`);
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('cacheLoadingError'), {}));
    } finally {
      this.clearOwnData();
      await this.setLoading(-1);
    }
  }
}
</script>
<style scoped>
.progress-enter-active, .progress-leave-active {
  transition: opacity .5s
}

.progress-enter, .progress-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0
}

.word-break {
  word-break: break-word;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
